import { cn } from "../../lib/utils";

export interface BrandFilterCategory {
  name: string;
  count: number;
}

export interface BrandFilterProductType {
  name: string;
  count?: number;
}

export interface BrandFilterOption {
  label: string;
  count?: number;
  /** Renders a right-aligned expand chevron (category nodes that drill in). */
  expandable?: boolean;
}

export interface BrandFilterFacetSection {
  title: string;
  options: BrandFilterOption[];
  /** Optional sub-group rendered under its own label (e.g. "Preorder by month"). */
  subGroup?: { title: string; options: BrandFilterOption[] };
  showMore?: boolean;
}

export interface BrandFilterRailProps {
  totalProducts: number;
  categories: BrandFilterCategory[];
  productTypes: BrandFilterProductType[];
  facetSections: BrandFilterFacetSection[];
  className?: string;
}

/**
 * The collapsible left filter rail from the brand storefront: an "All products"
 * count, a category tree, a searchable "Product types" facet, and the remaining
 * checkbox facet sections (price, ship window, GTIN, category). Pair with
 * {@link BrandFilterSortRow} (the All-filters/sort toggle row) and
 * {@link BrandFilterModal} (the compact small-screen treatment). Faithful to the
 * production brand storefront (b_tthhaz7z5x).
 */
export default function BrandFilterRail({
  totalProducts,
  categories,
  productTypes,
  facetSections,
  className,
}: BrandFilterRailProps) {
  return (
    <aside className={cn("hidden w-58 shrink-0 lg:block", className)}>
      <div className="sticky top-[176px] flex flex-col pt-2.5">
        <p className="type-fs-paragraph-medium text-fs-text-primary">Filters</p>

        {/* Category tree */}
        <div className="mt-4 flex flex-col">
          <button
            type="button"
            className="hover:bg-fs-action-surface-inverse-hover/50 flex w-full justify-between py-[3px] text-left"
          >
            <span className="flex flex-row gap-1">
              <span className="type-fs-paragraph-medium text-fs-text-primary underline underline-offset-4">
                All products
              </span>
              <span className="type-fs-paragraph text-fs-text-subdued">
                {totalProducts}
              </span>
            </span>
          </button>

          <div className="mt-6 flex flex-col gap-0.5">
            {categories.map((category) => (
              <div
                key={category.name}
                className="flex flex-row items-center justify-between gap-2"
              >
                <button
                  type="button"
                  className="hover:bg-fs-action-surface-inverse-hover/50 flex flex-1 justify-between py-1 text-left"
                >
                  <span className="flex flex-row gap-1">
                    <span className="type-fs-paragraph text-fs-text-primary">
                      {category.name}
                    </span>
                    <span className="type-fs-paragraph text-fs-text-subdued">
                      {category.count}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Expand ${category.name}`}
                  className="text-fs-text-subdued flex h-6 w-6 items-center justify-center"
                >
                  <ChevronDownIcon />
                </button>
              </div>
            ))}
          </div>
        </div>

        <FacetDivider />

        {/* Product types */}
        <FacetSection title="Product types">
          <div className="border-fs-border-muted flex items-center gap-2 rounded-full border px-4 py-2">
            <SearchIcon />
            <input
              type="search"
              placeholder="Search"
              aria-label="Search product types"
              className="type-fs-paragraph text-fs-text-primary w-full bg-transparent focus-visible:outline-none"
            />
          </div>
          <div className="mt-4 flex flex-col gap-3">
            {productTypes.map((type) => (
              <CheckboxOption
                key={type.name}
                label={type.name}
                count={type.count}
              />
            ))}
          </div>
          <ShowMoreLink />
        </FacetSection>

        {/* Remaining facet sections */}
        {facetSections.map((section) => (
          <div key={section.title}>
            <FacetDivider />
            <FacetSection title={section.title}>
              <div className="flex flex-col gap-3">
                {section.options.map((option) => (
                  <CheckboxOption
                    key={option.label}
                    label={option.label}
                    count={option.count}
                    expandable={option.expandable}
                  />
                ))}
              </div>
              {section.subGroup ? (
                <>
                  <p className="type-fs-paragraph-medium text-fs-text-primary mt-4">
                    {section.subGroup.title}
                  </p>
                  <div className="mt-3 flex flex-col gap-3">
                    {section.subGroup.options.map((option) => (
                      <CheckboxOption key={option.label} label={option.label} />
                    ))}
                  </div>
                </>
              ) : null}
              {section.showMore ? <ShowMoreLink /> : null}
            </FacetSection>
          </div>
        ))}
      </div>
    </aside>
  );
}

export interface BrandFilterSortRowProps {
  pills: string[];
  filtersOpen: boolean;
  onToggleFilters: () => void;
  /** Label for the sort control. Defaults to "Sort by Featured". */
  sortLabel?: string;
}

/** The All-filters/Hide-filters toggle, the scrollable category pills, and the sort control. */
export function BrandFilterSortRow({
  pills,
  filtersOpen,
  onToggleFilters,
  sortLabel = "Sort by Featured",
}: BrandFilterSortRowProps) {
  return (
    <div className="hidden w-full flex-row items-center justify-between gap-2 lg:flex">
      <div className="flex min-w-0 flex-1 flex-row items-center">
        <button
          type="button"
          aria-pressed={filtersOpen}
          onClick={onToggleFilters}
          className={cn(
            "type-fs-paragraph flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 whitespace-nowrap",
            filtersOpen
              ? "bg-fs-surface-primary-inverse text-fs-text-primary-inverse border-transparent"
              : "border-fs-border-muted text-fs-text-primary"
          )}
        >
          {filtersOpen ? <ChevronLeftIcon /> : <FilterIcon />}
          <span>{filtersOpen ? "Hide filters" : "All filters"}</span>
        </button>
        <div className="ml-2 flex min-w-0 flex-1 flex-row gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {pills.map((pill) => (
            <FilterButton key={pill}>
              <span>{pill}</span>
            </FilterButton>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="border-fs-border-muted text-fs-text-primary type-fs-paragraph ml-auto flex shrink-0 items-center gap-2 rounded-full border px-3 py-2"
      >
        {sortLabel}
        <ChevronDownIcon />
      </button>
    </div>
  );
}

const DEFAULT_SORT_OPTIONS = ["Featured", "Arranged by brand", "Newest"];

const DEFAULT_MODAL_CATEGORIES: {
  label: string;
  count: string;
  expandable?: boolean;
}[] = [
  { label: "All products", count: "100+" },
  { label: "Garden & outdoor", count: "50+", expandable: true },
  { label: "Home accents", count: "10+", expandable: true },
];

export interface BrandFilterModalProps {
  open: boolean;
  onClose: () => void;
  totalProducts: number;
  productTypes: BrandFilterProductType[];
  /** Sort radio options. Defaults to Featured / Arranged by brand / Newest. */
  sortOptions?: string[];
  /** Category radio options. Defaults to the Jungle Club sample set. */
  categories?: { label: string; count: string; expandable?: boolean }[];
  /** Applied-filter count shown in the header. Defaults to 1. */
  appliedCount?: number;
}

/** Compact filter modal shown below xl (the smaller-screen filter convention). */
export function BrandFilterModal({
  open,
  onClose,
  totalProducts,
  productTypes,
  sortOptions = DEFAULT_SORT_OPTIONS,
  categories = DEFAULT_MODAL_CATEGORIES,
  appliedCount = 1,
}: BrandFilterModalProps) {
  if (!open) {
    return null;
  }
  return (
    <div
      className="bg-fs-surface-primary-inverse/40 fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Filters"
        onClick={(event) => event.stopPropagation()}
        className="bg-fs-surface-primary flex max-h-[85vh] w-[560px] max-w-full flex-col rounded-lg"
      >
        {/* Header */}
        <div className="flex flex-col gap-4 p-6 pb-4">
          <div className="flex items-start justify-end">
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="text-fs-text-primary flex h-6 w-6 items-center justify-center"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <p className="type-fs-paragraph-medium text-fs-text-primary">
              {appliedCount} filter applied
            </p>
            <button
              type="button"
              className="type-fs-paragraph text-fs-text-primary underline"
            >
              Clear all
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col overflow-y-auto px-6">
          {/* Sort by */}
          <ModalSection title="Sort by">
            <div className="flex flex-col gap-3">
              {sortOptions.map((option, index) => (
                <RadioOption key={option} label={option} selected={index === 0} />
              ))}
            </div>
            <ShowMoreLink />
          </ModalSection>

          <FacetDivider />

          {/* Category */}
          <ModalSection title="Category" action="Clear">
            <div className="flex flex-col gap-3">
              {categories.map((category, index) => (
                <RadioOption
                  key={category.label}
                  label={category.label}
                  count={category.count}
                  selected={index === 0}
                  expandable={category.expandable}
                />
              ))}
            </div>
            <ShowMoreLink />
          </ModalSection>

          <FacetDivider />

          {/* Product types */}
          <ModalSection title="Product types">
            <div className="border-fs-border-muted flex items-center gap-2 rounded-full border px-4 py-2">
              <SearchIcon />
              <input
                type="search"
                placeholder="Search"
                aria-label="Search product types"
                className="type-fs-paragraph text-fs-text-primary w-full bg-transparent focus-visible:outline-none"
              />
            </div>
            <div className="mt-4 flex flex-col gap-3">
              {productTypes.map((type) => (
                <CheckboxOption
                  key={type.name}
                  label={type.name}
                  count={type.count}
                />
              ))}
            </div>
          </ModalSection>
        </div>

        {/* Footer */}
        <div className="p-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-fs-surface-primary-inverse text-fs-text-primary-inverse type-fs-paragraph-medium w-full rounded-full py-3"
          >
            Show {totalProducts} matches
          </button>
        </div>
      </div>
    </div>
  );
}

function FacetSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col py-4">
      <p className="type-fs-paragraph-medium text-fs-text-primary mb-4">
        {title}
      </p>
      {children}
    </div>
  );
}

function FacetDivider() {
  return <hr className="border-fs-border-muted border-t" />;
}

function ShowMoreLink() {
  return (
    <button
      type="button"
      className="type-fs-paragraph text-fs-text-primary mt-4 w-fit underline"
    >
      Show more
    </button>
  );
}

function CheckboxOption({
  label,
  count,
  expandable = false,
}: {
  label: string;
  count?: number;
  expandable?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2">
      <span className="border-fs-border-subdued h-5 w-5 shrink-0 rounded border" />
      <span className="type-fs-paragraph text-fs-text-primary flex-1">
        {label}
      </span>
      {count !== undefined ? (
        <span className="type-fs-paragraph text-fs-text-subdued">{count}</span>
      ) : null}
      {expandable ? (
        <span className="text-fs-text-primary flex h-5 w-5 items-center justify-center">
          <ChevronDownIcon />
        </span>
      ) : null}
    </label>
  );
}

function ModalSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col py-4">
      <div className="mb-4 flex items-center justify-between">
        <p className="type-fs-paragraph-medium text-fs-text-primary">{title}</p>
        {action ? (
          <button
            type="button"
            className="type-fs-paragraph text-fs-text-primary underline"
          >
            {action}
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function RadioOption({
  label,
  count,
  selected = false,
  expandable = false,
}: {
  label: string;
  count?: string;
  selected?: boolean;
  expandable?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2">
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected
            ? "border-fs-action-border-active"
            : "border-fs-border-subdued"
        )}
      >
        {selected ? (
          <span className="bg-fs-surface-primary-inverse h-2.5 w-2.5 rounded-full" />
        ) : null}
      </span>
      <span className="type-fs-paragraph text-fs-text-primary flex-1">
        {label}
      </span>
      {count ? (
        <span className="type-fs-paragraph text-fs-text-subdued">{count}</span>
      ) : null}
      {expandable ? (
        <span className="text-fs-text-subdued flex h-5 w-5 items-center justify-center">
          <ChevronDownIcon />
        </span>
      ) : null}
    </label>
  );
}

function FilterButton({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="border-fs-border-muted text-fs-text-primary type-fs-paragraph flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 whitespace-nowrap"
    >
      {children}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16.71 16.71A9.287 9.287 0 1 1 3.577 3.576 9.287 9.287 0 0 1 16.71 16.71Zm0 0 6.433 6.433" />
    </svg>
  );
}

// Matches the production "All filters" pill icon exactly — the legacy
// `@faire/web/slate/icons/Filter` glyph (sliders with knobs), viewBox 0 0 12 12.
function FilterIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9.30001 9.66659H12V10.3333H9.30001C9.14559 11.094 8.47299 11.6666 7.66667 11.6666C6.74619 11.6666 6 10.9204 6 9.99992C6 9.07944 6.74619 8.33325 7.66667 8.33325C8.47299 8.33325 9.14559 8.90584 9.30001 9.66659ZM5.30001 5.66659H12V6.33325H5.30001C5.14559 7.094 4.47299 7.66659 3.66667 7.66659C2.74619 7.66659 2 6.92039 2 5.99992C2 5.07944 2.74619 4.33325 3.66667 4.33325C4.47299 4.33325 5.14559 4.90584 5.30001 5.66659ZM10.6333 1.66659H12V2.33325H10.6333C10.4789 3.094 9.80632 3.66659 9 3.66659C8.07953 3.66659 7.33333 2.92039 7.33333 1.99992C7.33333 1.07944 8.07953 0.333252 9 0.333252C9.80632 0.333252 10.4789 0.905839 10.6333 1.66659ZM0 2.33325V1.66659H7.33333V2.33325H0ZM9 2.99992C9.55229 2.99992 10 2.5522 10 1.99992C10 1.44763 9.55229 0.999919 9 0.999919C8.44772 0.999919 8 1.44763 8 1.99992C8 2.5522 8.44772 2.99992 9 2.99992ZM0 6.33325V5.66659H2V6.33325H0ZM3.66667 6.99992C4.21895 6.99992 4.66667 6.5522 4.66667 5.99992C4.66667 5.44763 4.21895 4.99992 3.66667 4.99992C3.11438 4.99992 2.66667 5.44763 2.66667 5.99992C2.66667 6.5522 3.11438 6.99992 3.66667 6.99992ZM0 10.3333V9.66659H6V10.3333H0ZM7.66667 10.9999C8.21895 10.9999 8.66667 10.5522 8.66667 9.99992C8.66667 9.44763 8.21895 8.99992 7.66667 8.99992C7.11438 8.99992 6.66667 9.44763 6.66667 9.99992C6.66667 10.5522 7.11438 10.9999 7.66667 10.9999Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 12 7"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 1 6 6 11 1" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 7 12"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 1 1 6 6 11" />
    </svg>
  );
}
